import gettext, { pgettext } from '@sdk/lang/gettext';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import Text11 from '@sdk/components/ui/typography/Text11';
import Text14 from '@sdk/components/ui/typography/Text14';
import Text16 from '@sdk/components/ui/typography/Text16';
import Column from '@sdk/components/ui/spacing/Column';
import Row from '@sdk/components/ui/spacing/Row';
import HorizontalSeparator from '@sdk/components/ui/HorizontalSeparator';
import { SUBSCRIPTION_NAMES, PAYMENT_TYPE_NAMES, PAYMENT_TYPES } from '@sdk/account/constants';
import MoreOptionsMenu from '@sdk/components/ui/MoreOptionsMenu';
import { ButtonType } from '@sdk/components/ui/buttons/ButtonType';
import FooterSaveActions from '@sdk/components/ui/FooterSaveActions';
import leftPadZeros from '@sdk/utils/leftPadZeros';
import StatusLabel, { StatusLabelType } from '@sdk/components/ui/StatusLabel';
import dateToHumanFormat from '@sdk/utils/dateFormat/dateToHumanFormat';
import useConfig from '@sdk/config/useConfig';
import type { CurrencyType } from '@sdk/common/currency/types/CurrencyType';
import type { OrgSettings } from '@sdk/authentication/types/OrganisationType';

import CreditCardLogo from '@web/account/purchase/CreditCardLogo/CreditCardLogo';
import CurrentBillingPricing from '@web/account/billing/CurrentBillingPricing/CurrentBillingPricing';
import UpgradeSubscriptionModal from '@web/account/billing/UpgradeSubscriptionModal/UpgradeSubscriptionModal';
import DowngradeSubscriptionModal from '@web/account/billing/DowngradeSubscriptionModal/DowngradeSubscriptionModal';
import CreditCardForm from '@web/account/purchase/CreditCardForm/CreditCardForm';
import PauseSubscriptionModal from '@web/account/billing/PauseSubscriptionModal/PauseSubscriptionModal';
import EditBillingTopTip from '@web/account/billing/EditBillingTopTip/EditBillingTopTip';
import type { SubscriptionUtilsType } from '@web/account/types';
import { BillingInformationSection } from '@web/account/billing/BillingInformationSection/BillingInformationSection';
import { EditBillingInformation } from '@web/account/billing/EditBillingInformation/EditBillingInformation';

import { useEditBilling, useTaxTopTip } from './editBilling.hooks';
import {
  EditBillingContainer,
  CreditCardFields,
  CurrentPlanRow,
  EditBillingButton,
  EditBillingProgressButton,
  StatusLabelContainer,
  CreditCardDisplayNumber,
  EditCreditCardNumberIcon,
} from './editBilling.styles';

type Props = {
  subscriptionUtils: SubscriptionUtilsType;
  currency: CurrencyType;
  orgSettings: OrgSettings;
  goToPricing: () => void;
};

const EditBilling = ({ subscriptionUtils, currency, orgSettings, goToPricing }: Props) => {
  const {
    billingInfoContainerRef,
    updateSubscriptionModalRef,
    downgradeSubscriptionModalRef,
    emails,
    setEmails,
    manageSubscriptionOptions,
    manageSubscriptionOptionClicked,
    upgradeToBusinessPickerOption,
    onSaveHandle,
    onCancelHandle,
    isCreditCardEditMode,
    setIsCreditCardEditMode,
    isBillingInformationEditMode,
    setIsBillingInformationEditMode,
    onAddressChange,
    isBillingChanged,
    areChangesValid,
    billingInfo,
    isExternalPaymentType,
    pauseSubscriptionModalState,
    onResumeSubscriptionHandle,
    retryPayment,
    isCreditCardExpired,
    detailsText,
    setBillingDetails,
  } = useEditBilling({
    subscriptionUtils,
    goToPricing,
  });

  const { hasTax, onTaxChange } = useTaxTopTip();

  const {
    FEATURE_FLAGS: { PAUSE_SUBSCRIPTION_MENU_ITEM },
  } = useConfig();

  let subscriptionLevelToDisplay = billingInfo.subscriptionLevel;
  if (billingInfo.scheduledToResumeDate) {
    subscriptionLevelToDisplay = billingInfo.pausedSubscriptionLevel;
  }
  if (!billingInfo.subscriptionStartDate && billingInfo.formerSubscriptionLevel) {
    subscriptionLevelToDisplay = billingInfo.formerSubscriptionLevel;
  }

  const isCreditCardPaymentType = billingInfo.paymentType == PAYMENT_TYPES.CREDIT_CARD;

  return (
    <>
      <EditBillingContainer>
        <Column mBottom={20}>
          <EditBillingTopTip
            isCreditCardExpired={isCreditCardExpired}
            subscriptionLevelToDisplay={subscriptionLevelToDisplay}
            hasTax={hasTax}
          />
        </Column>
        <Row alignItems="center">
          <Text16 semiBold themeColor={ThemeColorType.primary}>
            {gettext('Subscription plan')}
          </Text16>
          <Row mLeft={'auto'}>
            <MoreOptionsMenu
              noBorder={true}
              options={manageSubscriptionOptions}
              onClick={manageSubscriptionOptionClicked}
            />
          </Row>
        </Row>
        <HorizontalSeparator />
        <CurrentPlanRow>
          <Column>
            <Row>
              <Text14 semiBold themeColor={ThemeColorType.gray}>
                {SUBSCRIPTION_NAMES[subscriptionLevelToDisplay]}
                {' / '}
                {billingInfo.isYearlyPlan ? gettext('Annual') : gettext('Monthly')}
              </Text14>
              {Boolean(billingInfo.scheduledToResumeDate) && (
                <StatusLabelContainer>
                  <StatusLabel label={gettext('Paused')} type={StatusLabelType.warning} />
                </StatusLabelContainer>
              )}
              {Boolean(
                billingInfo.paymentFailedDate &&
                  !billingInfo.subscriptionStartDate &&
                  billingInfo.formerSubscriptionLevel
              ) && (
                <StatusLabelContainer>
                  <StatusLabel label={gettext('Suspended')} type={StatusLabelType.warning} />
                </StatusLabelContainer>
              )}
            </Row>
            {Boolean(billingInfo.scheduledToResumeDate) && (
              <Row>
                <Text11 themeColor={ThemeColorType.gray}>{gettext('Resuming on')}</Text11>
                <Text11 themeColor={ThemeColorType.gray} semiBold>
                  &nbsp;
                  {dateToHumanFormat(billingInfo.scheduledToResumeDate, false, /* autoYear */ true)}
                </Text11>
              </Row>
            )}
          </Column>
          {Boolean(billingInfo.scheduledToResumeDate) && (
            <Row mLeft={'auto'}>
              <EditBillingProgressButton
                buttonType={ButtonType.primary}
                onClick={onResumeSubscriptionHandle}>
                {gettext('Resume %(subscriptionName)s', {
                  subscriptionName: SUBSCRIPTION_NAMES[billingInfo.pausedSubscriptionLevel],
                })}
              </EditBillingProgressButton>
            </Row>
          )}
          {billingInfo.subscriptionLevel < 2 &&
            !billingInfo.scheduledToResumeDate &&
            billingInfo.subscriptionStartDate && (
              <Row mLeft={'auto'}>
                <EditBillingButton
                  buttonType={ButtonType.primary}
                  onClick={() => manageSubscriptionOptionClicked(upgradeToBusinessPickerOption)}>
                  {gettext('Upgrade to Business')}
                </EditBillingButton>
              </Row>
            )}
        </CurrentPlanRow>
        <HorizontalSeparator />
        <CurrentBillingPricing
          level={billingInfo.subscriptionLevel}
          isYearlyPlan={billingInfo.isYearlyPlan}
          subscriptionUtils={subscriptionUtils}
          orgSettings={orgSettings}
          currency={currency}
          hideNextChargeDate={!billingInfo.nextChargeDate}
          onTaxChange={onTaxChange}
        />
        {isExternalPaymentType || isCreditCardPaymentType ? (
          <>
            <Row mTop={20} mBottom={5}>
              <Text16 semiBold themeColor={ThemeColorType.primary}>
                {gettext('Payment information')}
              </Text16>
            </Row>
            <HorizontalSeparator />
          </>
        ) : null}
        {isExternalPaymentType ? (
          <Row alignItems="center" mTop={20}>
            {gettext('Your payment method is now')}&nbsp;
            <Text14 semiBold themeColor={ThemeColorType.gray}>
              {gettext(PAYMENT_TYPE_NAMES[billingInfo.paymentType])}
            </Text14>
          </Row>
        ) : (
          <>
            {isCreditCardPaymentType ? (
              <Row alignItems="center" mTop={20} mBottom={20}>
                {!isCreditCardEditMode ? (
                  <>
                    <Column>
                      <Text14 semiBold themeColor={ThemeColorType.gray}>
                        {gettext('Payment method')}
                        {isCreditCardExpired && (
                          <Text14 semiBold themeColor={ThemeColorType.danger}>
                            &nbsp;{pgettext('Payment method (credit card) failed', '[Expired]')}
                          </Text14>
                        )}
                        {!isCreditCardExpired && Boolean(billingInfo.paymentFailedDate) && (
                          <Text14 semiBold themeColor={ThemeColorType.danger}>
                            &nbsp;{pgettext('Payment method (credit card) failed', '[Failed]')}
                          </Text14>
                        )}
                      </Text14>
                      <Row alignItems="center">
                        <Row mRight={5} mLeft={-5}>
                          <CreditCardLogo
                            type={billingInfo.creditCard.type}
                            showActiveOnly={true}
                          />
                        </Row>
                        <CreditCardDisplayNumber
                          onClick={() => {
                            setIsCreditCardEditMode(true);
                          }}>
                          <Text14 themeColor={ThemeColorType.gray}>
                            •••• •••• •••• {billingInfo.creditCard.lastFour} •{' '}
                            {leftPadZeros(2, billingInfo.creditCard.expiryMonth)}/
                            {billingInfo.creditCard.expiryYear}
                          </Text14>
                          <EditCreditCardNumberIcon />
                        </CreditCardDisplayNumber>
                      </Row>
                    </Column>
                    <Row mLeft={'auto'}>
                      {billingInfo.paymentFailedDate ? (
                        <EditBillingProgressButton
                          buttonType={ButtonType.primary}
                          onClick={retryPayment}>
                          {gettext('Retry payment')}
                        </EditBillingProgressButton>
                      ) : (
                        <EditBillingButton
                          buttonType={isCreditCardExpired ? ButtonType.primary : ButtonType.default}
                          onClick={() => {
                            setIsCreditCardEditMode(true);
                          }}>
                          {gettext('Edit payment method')}
                        </EditBillingButton>
                      )}
                    </Row>
                  </>
                ) : (
                  <CreditCardFields>
                    <CreditCardForm isBillingAddressFormActive={isBillingInformationEditMode} />
                  </CreditCardFields>
                )}
              </Row>
            ) : null}

            <div ref={billingInfoContainerRef}>
              {!isBillingInformationEditMode ? (
                <BillingInformationSection
                  emails={emails}
                  onEdit={() => setIsBillingInformationEditMode(true)}
                />
              ) : (
                <EditBillingInformation
                  emails={emails}
                  setEmails={setEmails}
                  onAddressChange={onAddressChange}
                  billingDetails={detailsText}
                  setBillingDetails={setBillingDetails}
                  withSlingAutocomplete={!isCreditCardEditMode}
                />
              )}
            </div>
          </>
        )}
      </EditBillingContainer>
      <UpgradeSubscriptionModal
        ref={updateSubscriptionModalRef}
        currency={currency}
        orgSettings={orgSettings}
        subscriptionUtils={subscriptionUtils}
      />
      <DowngradeSubscriptionModal
        ref={downgradeSubscriptionModalRef}
        subscriptionUtils={subscriptionUtils}
        orgSettings={orgSettings}
        currency={currency}
      />
      {Boolean(PAUSE_SUBSCRIPTION_MENU_ITEM) && (
        <PauseSubscriptionModal {...pauseSubscriptionModalState} />
      )}
      {isBillingChanged && (
        <FooterSaveActions
          onSaveClick={(): Promise<void> => onSaveHandle()}
          onCancelClick={(): void => onCancelHandle()}
          isValid={areChangesValid}
          allowSaveCallbackIfInvalid={true}
        />
      )}
    </>
  );
};

export default EditBilling;
